import { useInfiniteQuery } from "@tanstack/react-query";
import { templateCategoryService } from "../api/services/templateCategoryService";
import type {
  TemplateCategory,
  TemplateCategoryFilters,
  TemplateCategoryResult,
} from "../schemas/templateCategorySchema";
import { useTemplateCategories } from "./useTemplateCategories";
import { DEFAULT_PAGE_SIZE } from "../constants";

export type TemplateCategoriesInfiniteFilters = Omit<
  TemplateCategoryFilters,
  "page" | "pageSize"
> & {
  pageSize?: number;
};

export const useTemplateCategoriesInfinite = (
  filters: TemplateCategoriesInfiniteFilters = {},
) => {
  const { pageSize = DEFAULT_PAGE_SIZE, ...rest } = filters;
  const mutations = useTemplateCategories(undefined, { enabled: false });

  const query = useInfiniteQuery<TemplateCategoryResult, Error>({
    queryKey: ["templateCategories", "infinite", { ...rest, pageSize }],
    queryFn: ({ pageParam }) =>
      templateCategoryService.getAll({
        ...rest,
        page: pageParam as number,
        pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? (lastPage.page ?? 0) + 1 : undefined,
  });

  const templateCategories: TemplateCategory[] =
    query.data?.pages.flatMap((p) => p.templateCategories) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;

  return {
    ...mutations,
    templateCategories,
    total,
    isLoading: query.isLoading || mutations.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};
